import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FormMetricsService } from 'services/form-builder/form-metrics/form-metrics.service';
import { FormMetricsCommentItem } from 'services/models/form-builder/form-metrics/chart-data/comments-chart-data.model';
import { FormMetricsCommentsPageModel } from 'services/models/form-builder/form-metrics/form-metrics-comments-page.model';
import { FormMetricsFilter } from 'services/models/form-builder/form-metrics/form-metrics-filter.model';

/** Signature for a custom comments fetcher that replaces the default generic endpoint. */
export type FormMetricsCommentsFetcher = (
  formItemId: string,
  filters: FormMetricsFilter,
  page: number,
  pageSize: number
) => Observable<FormMetricsCommentsPageModel>;

export interface FormMetricsCommentViewItem extends FormMetricsCommentItem {
  isHighlighted?: boolean;
}

@Component({
  selector: 'app-form-metrics-comments-modal',
  templateUrl: './form-metrics-comments-modal.component.html',
  styleUrls: ['./form-metrics-comments-modal.component.scss'],
})
export class FormMetricsCommentsModalComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() formId: string;
  @Input() formItemId: string;
  @Input() title = 'Comentários';
  @Input() filters: FormMetricsFilter = {};
  @Input() highlightedEmployeeId: string | null = null;
  /** Comentários do respondente já obtidos via respondent-answers (sem HTTP extra). */
  @Input() highlightedComments: FormMetricsCommentItem[] = [];
  /** Fetcher customizado para substituir o endpoint genérico (ex: período probatório). */
  @Input() commentsFetcher: FormMetricsCommentsFetcher | null = null;

  @ViewChild('scrollBody') scrollBody?: ElementRef<HTMLElement>;
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLElement>;

  items: FormMetricsCommentViewItem[] = [];
  total = 0;
  page = 1;
  pageSize = 20;
  isLoading = false;
  isLoadingMore = false;
  hasError = false;
  hasMore = false;

  private destroy$ = new Subject<void>();
  private scrollObserver?: IntersectionObserver;
  private scrollObserverNeedsRefresh = false;

  constructor(
    public activeModal: NgbActiveModal,
    private formMetricsService: FormMetricsService
  ) {}

  ngOnInit(): void {
    if (this.highlightedEmployeeId) {
      this.applyHighlightedOnly();
      return;
    }

    this.loadComments(false);
  }

  ngAfterViewChecked(): void {
    if (this.scrollObserverNeedsRefresh) {
      this.scrollObserverNeedsRefresh = false;
      this.refreshScrollObserver();
    }
  }

  ngOnDestroy(): void {
    this.disconnectScrollObserver();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDismiss(): void {
    this.activeModal.close(false);
  }

  retry(): void {
    if (this.highlightedEmployeeId) {
      this.applyHighlightedOnly();
      return;
    }
    this.page = 1;
    this.items = [];
    this.loadComments(false);
  }

  private applyHighlightedOnly(): void {
    const highlighted = this.highlightedComments || [];
    this.items = highlighted.map(item => ({ ...item, isHighlighted: true }));
    this.total = highlighted.length;
    this.hasMore = false;
    this.isLoading = false;
    this.hasError = false;
  }

  private get baseFilters(): FormMetricsFilter {
    const filters = { ...(this.filters || {}) };
    delete filters.employeeId;
    return filters;
  }

  private loadComments(append: boolean): void {
    if (!this.formItemId) {
      return;
    }

    // Quando não há fetcher customizado, exige formId (endpoint genérico)
    if (!this.commentsFetcher && !this.formId) {
      return;
    }

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
    }
    this.hasError = false;

    const source$ = this.commentsFetcher
      ? this.commentsFetcher(this.formItemId, this.baseFilters, this.page, this.pageSize)
      : this.formMetricsService.getComments(this.formId, this.formItemId, this.baseFilters, this.page, this.pageSize);

    source$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe(
        result => {
          const nextItems = (result.items || []).map(item => ({
            ...item,
            isHighlighted: false,
          }));
          this.total = result.total || 0;
          this.page = result.page || this.page;
          this.pageSize = result.pageSize || this.pageSize;
          this.items = append ? [...this.items, ...nextItems] : nextItems;
          this.hasMore = this.items.length < this.total;
          this.scrollObserverNeedsRefresh = true;
        },
        () => {
          if (append) {
            this.page = Math.max(1, this.page - 1);
            this.scrollObserverNeedsRefresh = true;
            return;
          }
          this.hasError = true;
          this.items = [];
          this.hasMore = false;
        }
      );
  }

  private loadMore(): void {
    if (
      this.highlightedEmployeeId ||
      !this.hasMore ||
      this.isLoading ||
      this.isLoadingMore ||
      this.hasError
    ) {
      return;
    }

    this.page += 1;
    this.loadComments(true);
  }

  private refreshScrollObserver(): void {
    this.disconnectScrollObserver();
    if (!this.hasMore || this.highlightedEmployeeId) {
      return;
    }

    const root = this.scrollBody?.nativeElement;
    const sentinel = this.scrollSentinel?.nativeElement;
    if (!root || !sentinel) {
      return;
    }

    this.scrollObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadMore();
        }
      },
      { root, rootMargin: '80px 0px', threshold: 0 }
    );
    this.scrollObserver.observe(sentinel);
  }

  private disconnectScrollObserver(): void {
    this.scrollObserver?.disconnect();
    this.scrollObserver = undefined;
  }
}
